import { useState, useEffect } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { Menu, X, Search, Globe, ChevronDown } from 'lucide-react'
import { cn } from '../utils/cn'

const navLinks = [
  { name: 'Courses', href: '#courses', mega: true },
  { name: 'Roadmaps', href: '#roadmaps' },
  { name: 'Community', href: '#community' },
  { name: 'Pricing', href: '#pricing' },
  { name: 'Blog', href: '#blog' },
]

const courseCategories = [
  { name: 'Web Development', courses: ['React', 'Next.js', 'TypeScript', 'Node.js'] },
  { name: 'Mobile Development', courses: ['React Native', 'Flutter', 'Swift', 'Kotlin'] },
  { name: 'Data Science', courses: ['Python', 'Machine Learning', 'Data Analysis', 'SQL'] },
  { name: 'DevOps', courses: ['Docker', 'Kubernetes', 'AWS', 'CI/CD'] },
]

export function Navigation() {
  const [isScrolled, setIsScrolled] = useState(false)
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false)
  const [activeMegaMenu, setActiveMegaMenu] = useState<string | null>(null)

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50)
    }
    window.addEventListener('scroll', handleScroll, { passive: true })
    return () => window.removeEventListener('scroll', handleScroll)
  }, [])

  return (
    <>
      <motion.nav
        initial={{ y: -100 }}
        animate={{ y: 0 }}
        transition={{ duration: 0.6, ease: [0.16, 1, 0.3, 1] }}
        className={cn(
          'fixed top-0 left-0 right-0 z-50 transition-all duration-500',
          isScrolled ? 'glass py-3' : 'bg-transparent py-6'
        )}
      >
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="flex items-center justify-between">
            {/* Logo */}
            <motion.a
              href="#"
              className="flex items-center gap-2 group"
              whileHover={{ scale: 1.02 }}
            >
              <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-primary to-secondary flex items-center justify-center">
                <span className="text-background font-bold text-xl">C</span>
              </div>
              <span className="text-xl font-bold text-white group-hover:text-primary transition-colors">
                CodeNest
              </span>
            </motion.a>

            {/* Desktop Navigation */}
            <div className="hidden lg:flex items-center gap-8">
              {navLinks.map((link) => (
                <div
                  key={link.name}
                  className="relative"
                  onMouseEnter={() => link.mega && setActiveMegaMenu(link.name)}
                  onMouseLeave={() => setActiveMegaMenu(null)}
                >
                  <a
                    href={link.href}
                    className="flex items-center gap-1 text-sm font-medium text-white/80 hover:text-white transition-colors"
                  >
                    {link.name}
                    {link.mega && <ChevronDown className="w-4 h-4" />}
                  </a>
                  
                  {/* Mega Menu */}
                  <AnimatePresence>
                    {link.mega && activeMegaMenu === link.name && (
                      <motion.div
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: 10 }}
                        transition={{ duration: 0.2 }}
                        className="absolute top-full left-1/2 -translate-x-1/2 pt-4"
                      >
                        <div className="glass-strong rounded-2xl p-6 w-[600px] grid grid-cols-2 gap-6">
                          {courseCategories.map((category) => (
                            <div key={category.name}>
                              <h4 className="text-sm font-semibold text-primary mb-3">
                                {category.name}
                              </h4>
                              <ul className="space-y-2">
                                {category.courses.map((course) => (
                                  <li key={course}>
                                    <a
                                      href="#"
                                      className="text-sm text-white/60 hover:text-white transition-colors"
                                    >
                                      {course}
                                    </a>
                                  </li>
                                ))}
                              </ul>
                            </div>
                          ))}
                        </div>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>
              ))}
            </div>

            {/* Right Side */}
            <div className="hidden lg:flex items-center gap-4">
              <button className="p-2 text-white/60 hover:text-white transition-colors">
                <Search className="w-5 h-5" />
              </button>
              <button className="p-2 text-white/60 hover:text-white transition-colors">
                <Globe className="w-5 h-5" />
              </button>
              <motion.a
                href="#contact"
                className="px-5 py-2.5 rounded-full bg-white text-background font-medium text-sm hover:bg-white/90 transition-colors"
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
              >
                Get Started
              </motion.a>
            </div>

            {/* Mobile Menu Button */}
            <button
              className="lg:hidden p-2 text-white"
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            >
              {isMobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>
        </div>
      </motion.nav>

      {/* Mobile Menu */}
      <AnimatePresence>
        {isMobileMenuOpen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-40 lg:hidden"
          >
            <div className="absolute inset-0 bg-background/95 backdrop-blur-xl" />
            <motion.div
              initial={{ x: '100%' }}
              animate={{ x: 0 }}
              exit={{ x: '100%' }}
              transition={{ type: 'spring', damping: 25, stiffness: 200 }}
              className="absolute inset-y-0 right-0 w-full max-w-sm glass-strong p-8 pt-24"
            >
              <div className="flex flex-col gap-6">
                {navLinks.map((link, index) => (
                  <motion.a
                    key={link.name}
                    href={link.href}
                    initial={{ opacity: 0, x: 20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: index * 0.1 }}
                    className="text-2xl font-medium text-white hover:text-primary transition-colors"
                    onClick={() => setIsMobileMenuOpen(false)}
                  >
                    {link.name}
                  </motion.a>
                ))}
                <motion.a
                  href="#contact"
                  initial={{ opacity: 0, x: 20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.5 }}
                  className="mt-4 px-6 py-3 rounded-full bg-white text-background font-medium text-center"
                >
                  Get Started
                </motion.a>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  )
}